import React, { useState, useEffect } from 'react';
import { Invoice, InvoiceItem, calculateInvoiceTotals, generateId } from '../models/invoice';
import { CompanyDetails as CompanyDetailsType, ClientDetails as ClientDetailsType, InvoiceMeta as InvoiceMetaType } from '../models/invoice'; // Import types for clarity
import CompanyDetails from './CompanyDetails';
import ClientDetails from './ClientDetails';
import InvoiceMeta from './InvoiceMeta';
import InvoiceItems from './InvoiceItems';
import InvoiceSummary from './InvoiceSummary';
import PrintInvoice from './PrintInvoice';
import logoUrl from '../assets/logo.jpeg';

type HiddenFieldsState = {
    company: { [K in keyof CompanyDetailsType]?: boolean };
    client: { [K in keyof ClientDetailsType]?: boolean };
    meta: { [K in keyof InvoiceMetaType]?: boolean };
};

const initialHiddenFieldsState: HiddenFieldsState = {
    company: {
        name: false, address: false, vatCode: false, registrationCode: false,
        bankAccount: false, bankName: false, swift: false, email: false, phone: false
    },
    client: {
        name: false, address: false, vatCode: false, registrationCode: false,
        email: false, phone: false
    },
    meta: {
        series: false, number: false, date: false, dueDate: false
    },
};

const initialInvoiceState: Invoice = {
    company: {
        name: '', address: '', vatCode: '', registrationCode: '',
        bankAccount: '', bankName: '', swift: '', email: '', phone: '',
    },
    client: {
        name: '', address: '', vatCode: '', registrationCode: '',
        email: '', phone: '',
    },
    meta: {
        series: '', number: '', date: new Date().toISOString().split('T')[0], dueDate: '',
    },
    items: [
        {
            id: generateId(), name: 'Paslauga', unit: 'vnt.', quantity: 1, price: 0, vat: 21,
        },
    ],
};




const App: React.FC = () => {
    const [invoice, setInvoice] = useState<Invoice>(initialInvoiceState);
    const [hiddenFields, setHiddenFields] = useState<HiddenFieldsState>(initialHiddenFieldsState);
    const [companyLogo, setCompanyLogo] = useState<string | null>(null); // New state for company logo

    // Load invoice from localStorage on mount
    useEffect(() => {
        const savedInvoice = localStorage.getItem('fakturionas_data');
        if (savedInvoice) {
            try {
                const parsedInvoice = JSON.parse(savedInvoice);
                // Deep merge loaded invoice with initial state to handle missing properties gracefully
                setInvoice(prev => ({
                    ...initialInvoiceState,
                    ...parsedInvoice,
                    company: { ...initialInvoiceState.company, ...parsedInvoice.company },
                    client: { ...initialInvoiceState.client, ...parsedInvoice.client },
                    meta: { ...initialInvoiceState.meta, ...parsedInvoice.meta },
                    items: parsedInvoice.items || initialInvoiceState.items, // Ensure items is always an array
                }));
            } catch (e) {
                console.error('Failed to load saved invoice data', e);
            }
        }

        const savedHiddenFields = localStorage.getItem('fakturionas_hidden_fields');
        if (savedHiddenFields) {
            try {
                const parsedHiddenFields = JSON.parse(savedHiddenFields);
                // Merge with initial state to handle new fields gracefully
                setHiddenFields(prev => ({
                    company: { ...initialHiddenFieldsState.company, ...parsedHiddenFields.company },
                    client: { ...initialHiddenFieldsState.client, ...parsedHiddenFields.client },
                    meta: { ...initialHiddenFieldsState.meta, ...parsedHiddenFields.meta },
                }));
            } catch (e) {
                console.error('Failed to load saved hidden fields data', e);
            }
        }

        const savedCompanyLogo = localStorage.getItem('fakturionas_company_logo'); // Load company logo
        if (savedCompanyLogo) {
            setCompanyLogo(savedCompanyLogo);
        }
    }, []);

    // Save invoice to localStorage whenever invoice changes
    useEffect(() => {
        localStorage.setItem('fakturionas_data', JSON.stringify(invoice));
    }, [invoice]);

    // Save hiddenFields to localStorage whenever hiddenFields changes
    useEffect(() => {
        localStorage.setItem('fakturionas_hidden_fields', JSON.stringify(hiddenFields));
    }, [hiddenFields]);

    // Save companyLogo to localStorage whenever companyLogo changes
    useEffect(() => {
        if (companyLogo) {
            localStorage.setItem('fakturionas_company_logo', companyLogo);
        } else {
            localStorage.removeItem('fakturionas_company_logo');
        }
    }, [companyLogo]);

    const handleCompanyChange = (field: string, value: string) => {
        setInvoice(prev => ({
            ...prev,
            company: { ...prev.company, [field]: value }
        }));
    };

    const handleClientChange = (field: string, value: string) => {
        setInvoice(prev => ({
            ...prev,
            client: { ...prev.client, [field]: value }
        }));
    };

    const handleMetaChange = (field: string, value: string) => {
        setInvoice(prev => ({
            ...prev,
            meta: { ...prev.meta, [field]: value }
        }));
    };

    const toggleFieldVisibility = (section: keyof HiddenFieldsState, field: string) => {
        setHiddenFields(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: !prev[section]?.[field as keyof typeof prev[typeof section]],
            },
        }));
    };

    const handleItemChange = (index: number, field: string, value: string | number) => {
        setInvoice(prev => {
            const newItems = [...prev.items];
            newItems[index] = { ...newItems[index], [field]: value };
            return { ...prev, items: newItems };
        });
    };

    const handleAddItem = () => {
        const newItem: InvoiceItem = {
            id: generateId(),
            name: '',
            unit: 'vnt.',
            quantity: 1,
            price: 0,
            vat: 21,
        };
        setInvoice(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
    };

    const handleRemoveItem = (index: number) => {
        setInvoice(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handlePrint = () => {
        window.print();
    };

    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setCompanyLogo(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleClearLogo = () => {
        setCompanyLogo(null);
    };

    const totals = calculateInvoiceTotals(invoice.items || []);

    return (
        <>
            <div className="app-main-header">
                <div className="app-brand-section">
                    <img src={logoUrl} alt="Fakturionas Logo" className="app-logo" />
                    <h1 className="app-brand-name">Fakturionas</h1>
                </div>
                <div className="app-header-actions">
                    <button style={styles.btnPrint} onClick={handlePrint}>
                        <span>🖨️</span> Atsisiųsti PDF
                    </button>
                </div>
            </div>

            <div style={styles.mainContainer}>
                <div style={styles.invoiceEditor}>
                    <div style={styles.grid2}>
                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>Pardavėjas</div>
                            <CompanyDetails
                                company={invoice.company}
                                onChange={handleCompanyChange}
                                hiddenFields={hiddenFields.company}
                                onToggleVisibility={(field) => toggleFieldVisibility('company', field)}
                                companyLogo={companyLogo}
                                setCompanyLogo={setCompanyLogo}
                                onLogoUpload={handleLogoUpload}
                                onClearLogo={handleClearLogo}
                            />
                        </div>
                        <div style={styles.section}>
                            <div style={styles.sectionTitle}>Pirkėjas</div>
                            <ClientDetails
                                client={invoice.client}
                                onChange={handleClientChange}
                                hiddenFields={hiddenFields.client}
                                onToggleVisibility={(field) => toggleFieldVisibility('client', field)}
                            />
                        </div>
                    </div>

                    <div style={styles.section}>
                        <div style={styles.sectionTitle}>Sąskaitos informacija</div>
                        <InvoiceMeta
                            meta={invoice.meta}
                            onChange={handleMetaChange}
                            hiddenFields={hiddenFields.meta}
                            onToggleVisibility={(field) => toggleFieldVisibility('meta', field)}
                        />
                    </div>

                    <div style={styles.section}>
                        <div style={styles.sectionTitle}>Prekės ir paslaugos</div>
                        <InvoiceItems
                            items={invoice.items}
                            onItemChange={handleItemChange}
                            onAddItem={handleAddItem}
                            onRemoveItem={handleRemoveItem}
                            dataTestId="invoice-items-editor"
                        />
                    </div>

                    <div style={styles.section}>
                        <InvoiceSummary totals={totals} />
                    </div>
                </div>
            </div>

            <div className="print-invoice-wrapper">
                <PrintInvoice invoice={invoice} hiddenFields={hiddenFields} companyLogo={companyLogo} />
            </div>
        </>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    uploadButton: {
        backgroundColor: '#f0f0f0',
        color: '#333',
        border: '1px solid #ccc',
        padding: '10px 15px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    clearButton: {
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        padding: '10px 15px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 'bold',
    },
    mainContainer: {
        maxWidth: '1000px',
        margin: '40px auto',
        padding: '0 20px',
    },
    invoiceEditor: {
        background: '#fff',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    },
    grid2: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
        marginBottom: '40px',
    },
    section: {
        marginBottom: '30px',
    },
    sectionTitle: {
        fontSize: '1.1rem',
        fontWeight: 'bold',
        marginBottom: '15px',
        color: '#0056b3',
        borderBottom: '2px solid #f4f6f8',
        paddingBottom: '10px',
    },
    btnPrint: {
        backgroundColor: '#0056b3',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
};

export default App;